import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSessionManager } from "../hooks/useSessionManager";
import { useCurrentUser, clearCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../permissions";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import LanguageFlags from "./LanguageFlags";
import { APP_VERSION } from "../version";
import {
  LogoWordmark, IconDashboard, IconBranch, IconWarehouse, IconCatalog,
  IconCash, IconStaff, IconDevice, IconBuilding,
  IconChart, IconLogout, IconLock, IconSettings,
  IconMenu, IconX,
} from "./Icons";

type NavChild = { to: string; label: string; end?: boolean; perm?: string; anyOf?: string[]; roles?: string[] };
type NavItem = {
  to: string;
  label: string;
  icon: (props: { size?: number }) => JSX.Element;
  end?: boolean;
  perm?: string;
  anyOf?: string[];
  roles?: string[];
  children?: NavChild[];
};

function UserMenuItem({ to, icon: Icon, label, onClick }: { to: string; icon: (props: { size?: number }) => JSX.Element; label: string; onClick: () => void }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      style={({ isActive }) => ({
        display: "flex", alignItems: "center", gap: 10,
        padding: "8px 12px", borderRadius: "var(--radius-sm)",
        background: isActive ? "var(--accent-dim)" : "transparent",
        color: isActive ? "var(--accent)" : "var(--text)",
        textDecoration: "none", fontSize: 14,
      })}
    >
      <Icon size={18} /> {label}
    </NavLink>
  );
}


function getNav(t: (key: string) => string, eetEnabled: boolean, hasPerm: (key: string) => boolean, hasRole: (roles: string[]) => boolean): NavItem[] {
  const items: NavItem[] = [
    { to: "/", label: t("nav.dashboard"), icon: IconDashboard, end: true },
    { to: "/branches", label: t("nav.branches"), icon: IconBranch, perm: "branches" },
    {
      to: "/inventory",
      label: t("nav.warehouses"),
      icon: IconWarehouse,
      children: [
        { to: "/inventory", label: t("nav.inventoryLevels"), perm: "stock_levels" },
        { to: "/inventory/movements", label: t("nav.stockMovements"), perm: "stock_levels" },
        { to: "/suppliers", label: t("nav.suppliers") },
        { to: "/stock-receipts", label: t("nav.stockReceipt"), anyOf: ["stock_receipts", "stock_transfers", "stock_corrections"] },
        { to: "/stock-counts", label: t("nav.stockCounts"), perm: "stock_counts_view" },
        { to: "/warehouses", label: t("nav.warehouseManagement"), perm: "warehouses" },
      ],
    },
    {
      to: "/catalog/products",
      label: t("nav.catalog"),
      icon: IconCatalog,
      children: [
        { to: "/catalog/products", label: t("nav.products"), end: false },
        { to: "/catalog/categories", label: t("nav.categories") },
        { to: "/catalog/tags", label: t("nav.tags") },
      ],
      perm: "catalog",
    },
    {
      to: "/finance",
      label: t("nav.finance"),
      icon: IconCash,
      children: [
        { to: "/finance/receipts", label: t("nav.receipts"), perm: "accounts" },
        { to: "/finance/open-accounts", label: t("nav.openAccounts"), perm: "accounts" },
        { to: "/finance/cash-operations", label: t("nav.cashOperations"), perm: "cash_ops" },
        { to: "/finance/settlements", label: t("nav.settlements"), perm: "settlements" },
        ...(eetEnabled ? [{ to: "/finance/eet", label: t("nav.eet"), perm: "eet_history" }] : []),
      ],
    },
    { to: "/staff", label: t("nav.staff"), icon: IconStaff, perm: "staff" },
    {
      to: "/devices",
      label: t("nav.devices"),
      icon: IconDevice,
      children: [
        { to: "/devices", label: t("nav.devices") },
        { to: "/devices/license-history", label: t("nav.licenseHistory"), roles: ["owner"] },
      ],
      perm: "devices",
    },
    {
      to: "/operation-settings",
      label: t("nav.operationSettings"),
      icon: IconSettings,
      children: [
        { to: "/operation-settings", label: t("nav.opGeneral"), roles: ["owner", "manager"] },
        { to: "/operation-settings/permissions", label: t("nav.opPermissions"), perm: "pos_permissions" },
        ...(eetEnabled ? [{ to: "/operation-settings/eet", label: t("nav.opEet"), roles: ["owner", "manager"] }] : []),
      ],
    },
    {
      to: "/reports",
      label: t("nav.reports"),
      icon: IconChart,
      children: [
        { to: "/reports", label: t("nav.reportsOverview"), anyOf: ["revenue_overview", "attendance"] },
        { to: "/reports/export", label: t("nav.reportsExport"), perm: "exports" },
      ],
    },
  ];

  // Filter children by grants; drop sections with no visible children (or no
  // own grant when the section has no children).
  return items
    .map((n) => n.children ? { ...n, children: n.children.filter((c) => c.roles ? hasRole(c.roles) : c.perm ? hasPerm(c.perm) : c.anyOf ? c.anyOf.some(hasPerm) : true) } : n)
    .filter((n) => {
      if (n.perm && !hasPerm(n.perm)) return false;
      if (n.children) return n.children.length > 0;
      if (n.perm) return hasPerm(n.perm);
      if (n.anyOf) return n.anyOf.some(hasPerm);
      return true;
    });
}

export default function Layout() {
  const nav_hook = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const { data: tenantProfile } = useQuery({
    queryKey: ["tenantProfile"],
    queryFn: () => api.get<{ eet_mode?: string }>("/tenant/profile").then((r) => r.data),
    staleTime: 5 * 60_000,
  });
  const eetEnabled = !!tenantProfile && tenantProfile.eet_mode !== "disabled";
  const { user } = useCurrentUser();
  const nav = getNav(t, eetEnabled, (key) => can(user, key), (roles) => !!user && roles.includes(user.role));
  const [openSub, setOpenSub] = useState<string | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const sidebarExpanded = isMobile ? true : !collapsed;
  useSessionManager();

  // Active-route helpers: a section is highlighted when the current path is
  // its own route or any of its children's; among matching children the
  // longest (most specific) path wins.
  const isActivePath = (to: string, end = false) =>
    end ? location.pathname === to : location.pathname === to || location.pathname.startsWith(to + "/");
  const activeChildTo = (children: { to: string }[]) => {
    const matches = children.filter((c) => isActivePath(c.to));
    return matches.length ? matches.reduce((a, b) => (b.to.length > a.to.length ? b : a)).to : null;
  };
  const activeSection = (n: NavItem) =>
    isActivePath(n.to, n.end ?? false) || (n.children ? activeChildTo(n.children) !== null : false);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 900px)");
    const update = () => {
      setIsMobile(media.matches);
      setCollapsed(media.matches);
    };
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  // Auto-expand the sidebar section containing the current route — also on
  // direct URL entry / page refresh.
  useEffect(() => {
    const match = nav.find((n) => n.children && activeSection(n));
    setOpenSub(match ? match.to : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  function logout() {
    const rt = localStorage.getItem("lk_refresh");
    if (rt) {
      api.post("/auth/logout", { refresh_token: rt }).catch(() => {});
    }
    localStorage.removeItem("lk_access");
    localStorage.removeItem("lk_refresh");
    clearCurrentUser();
    nav_hook("/login");
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--bg)" }}>
      {/* ---- Sidebar ---- */}
      {isMobile && mobileMenuOpen && (
        <div
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.5)", zIndex: 199,
          }}
        />
      )}
      <aside style={{
        width: isMobile ? "var(--sidebar-w)" as any : (sidebarExpanded ? "var(--sidebar-w)" as any : 72),
        flexShrink: 0,
        background: "var(--bg-elevated)",
        borderRight: "1px solid var(--border)",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.2s ease, transform 0.2s ease",
        position: isMobile ? "fixed" : "sticky",
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: isMobile ? 200 : "auto",
        transform: isMobile ? (mobileMenuOpen ? "translateX(0)" : "translateX(-100%)") : "none",
        height: isMobile ? "100dvh" : "100vh",
      }}>
        {/* Logo */}
        <div style={{
          padding: "0 20px",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 64,
          boxSizing: "border-box",
          flexShrink: 0,
        }}>
          {sidebarExpanded && <LogoWordmark size={28} />}
          {isMobile ? (
            <button
              onClick={() => setMobileMenuOpen(false)}
              style={{ background: "transparent", border: "none", color: "var(--text)", cursor: "pointer", padding: 4 }}
            >
              <IconX size={24} />
            </button>
          ) : (
            <button
              onClick={() => setCollapsed(!collapsed)}
              title={collapsed ? t("nav.showPanel") : t("nav.hidePanel")}
              style={{
                background: "var(--accent-dim)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                color: "var(--accent)",
                cursor: "pointer",
                padding: "6px 8px",
                fontSize: 12,
                lineHeight: 1,
                margin: sidebarExpanded ? 0 : "0 auto",
                display: "flex",
                alignItems: "center",
              }}
            >
              {collapsed ? "▶" : "◀"}
            </button>
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: "12px 10px", display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}>
          {nav.map((n) => {
            const Icon = n.icon;
            const hasChildren = !!n.children;
            const isOpen = openSub === n.to;
            const sectionActive = activeSection(n);
            const childActive = n.children ? activeChildTo(n.children) : null;
            return (
              <div key={n.to}>
                <NavLink
                  to={hasChildren ? (n.children![0]?.to ?? n.to) : n.to}
                  end={n.end && !hasChildren}
                  onClick={(e) => {
                    if (hasChildren && sidebarExpanded) {
                      if (isOpen) {
                        // Open section collapses in place; never navigate
                        // to a parent path that may not exist.
                        e.preventDefault();
                        setOpenSub(null);
                        return;
                      }
                      // Closed section: expand and let NavLink navigate to
                      // the first visible child (its `to` above).
                      setOpenSub(n.to);
                    }
                    if (isMobile) {
                      setMobileMenuOpen(false);
                    }
                  }}
                  style={() => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 14px",
                    borderRadius: "var(--radius-sm)",
                    color: sectionActive ? "var(--accent)" : "var(--text-secondary)",
                    background: sectionActive ? "var(--accent-dim)" : "transparent",
                    fontWeight: sectionActive ? 600 : 500,
                    fontSize: 14,
                    transition: "all var(--transition)",
                    border: "none",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                  })}
                >
                  <Icon size={20} />
                  {sidebarExpanded && (
                    <>
                      <span style={{ flex: 1 }}>{n.label}</span>
                      {hasChildren && <span style={{ fontSize: 10, color: "var(--muted)" }}>{isOpen ? "▲" : "▼"}</span>}
                    </>
                  )}
                </NavLink>
                {sidebarExpanded && hasChildren && isOpen && (
                  <div style={{ paddingLeft: 18, marginTop: 4, display: "flex", flexDirection: "column", gap: 2 }}>
                    {(n.children || []).map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        end={c.end ?? true}
                        onClick={() => { if (isMobile) setMobileMenuOpen(false); }}
                        style={() => ({
                          display: "block",
                          padding: "8px 14px 8px 28px",
                          borderRadius: "var(--radius-sm)",
                          color: childActive === c.to ? "var(--accent)" : "var(--text-secondary)",
                          background: childActive === c.to ? "var(--accent-dim)" : "transparent",
                          fontWeight: childActive === c.to ? 600 : 400,
                          fontSize: 13,
                          textDecoration: "none",
                          borderLeft: childActive === c.to ? "2px solid var(--accent)" : "2px solid transparent",
                        })}
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Logout */}
        <div style={{ padding: "12px 10px", borderTop: "1px solid var(--border)" }}>
          <button className="ghost" onClick={logout} style={{
            width: "100%", justifyContent: sidebarExpanded ? "flex-start" : "center", gap: 12, color: "var(--muted)",
          }}>
            <IconLogout size={20} />
            {sidebarExpanded && <span>{t("nav.logout")}</span>}
          </button>
        </div>

        {/* Version */}
        {sidebarExpanded && (
          <div style={{ padding: "8px 16px", color: "var(--muted)", fontSize: 11, textAlign: "center" }}>
            Backoffice v{APP_VERSION}
          </div>
        )}
      </aside>

      {/* ---- Main ---- */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {/* Topbar */}
        <header style={{
          minHeight: 64,
          borderBottom: "1px solid var(--border)",
          background: "var(--bg-elevated)",
          display: "flex",
          alignItems: "center",
          flexWrap: isMobile ? "nowrap" : "wrap",
          padding: isMobile ? "8px 16px" : "0 28px",
          gap: 16,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}>
          {isMobile && (
            <button
              onClick={() => setMobileMenuOpen(v => !v)}
              style={{
                background: "transparent", border: "none", color: "var(--text)",
                cursor: "pointer", padding: 4, display: "flex", alignItems: "center",
              }}
              aria-label="Menu"
            >
              <IconMenu size={26} />
            </button>
          )}
          <div style={{ flex: 1 }} />
          <LanguageFlags />
          <div
            style={{ position: "relative" }}
            onMouseEnter={() => setUserMenuOpen(true)}
            onMouseLeave={() => setUserMenuOpen(false)}
          >
            <button
              onClick={() => setUserMenuOpen(v => !v)}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "6px 14px", borderRadius: 20, background: "var(--accent-dim)",
                border: "none", cursor: "pointer", color: "var(--text)",
              }}
            >
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                background: "var(--gradient-sunset)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 700, color: "#1a1200",
              }}>{(user?.full_name || user?.email || "?").charAt(0).toUpperCase()}</div>
              <span className="hide-mobile" style={{ fontSize: 13, fontWeight: 500 }}>
                {user?.full_name || user?.email || "—"}
              </span>
              <span className="hide-mobile" style={{ fontSize: 10, color: "var(--muted)", marginLeft: 2 }}>{userMenuOpen ? "▲" : "▼"}</span>
            </button>
            {userMenuOpen && (
              <div style={{
                position: "absolute", top: "100%", right: 0,
                minWidth: 200, paddingTop: 6, zIndex: 20,
              }}>
                <div style={{
                  background: "var(--bg-elevated)", border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)", boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  padding: "6px",
                }}>
                  <UserMenuItem to="/profile" icon={IconBuilding} label={t("nav.companyProfile")} onClick={() => setUserMenuOpen(false)} />
                  {user?.role === "owner" && (
                    <UserMenuItem to="/users" icon={IconStaff} label={t("nav.users")} onClick={() => setUserMenuOpen(false)} />
                  )}
                  <UserMenuItem to="/change-password" icon={IconLock} label={t("nav.changePassword")} onClick={() => setUserMenuOpen(false)} />
                  <div style={{ borderTop: "1px solid var(--border)", margin: "4px 0" }} />
                  <button onClick={() => { setUserMenuOpen(false); logout(); }} style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 12px", borderRadius: "var(--radius-sm)", border: "none",
                    background: "transparent", color: "var(--text)", cursor: "pointer", fontSize: 14,
                  }}>
                    <IconLogout size={18} /> {t("nav.logout")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Content */}
        <main style={{ flex: 1, padding: isMobile ? "16px" : "32px 28px", overflow: "auto", minWidth: 0 }}>
          <div className="fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
